<template>
    <!-- Global drag overlay - shown when files are dragged anywhere on page -->
    <div v-if="isFileDraggedGlobally" class="drop-overlay" @dragover.prevent @drop="dropFile($event)"
        @dragleave="handleGlobalDragLeave">
        <div class="drop-overlay__content">
            <div class="drop-overlay__icon"></div>
            <div class="drop-overlay__text">{{ $t(`dropFile.${"dropHere"}`) || "Drop your file here" }}</div>
        </div>
    </div>

    <!-- Original drop zone component -->
    <div class="drop" :class="{ 'drop--over': isDragOver }" @dragenter="dragging($event)" @dragleave="dragLeave($event)"
        @dragover.prevent @drop="dropFile($event)">
        <div class="drop__icon" v-if="!attachments.length"></div>

        <div class="files" :class="{
            'files--has-types': documentTypes.length,
            'files--deletable': deletable,
        }" v-else>
            <div class="files__header">
                <div class="files__heading">File</div>
                <div v-if="documentTypes.length" class="files__heading">File type</div>
                <div v-if="deletable" class="files__heading files__heading--action">Remove</div>
            </div>

            <div class="file" v-for="(item, index) in attachments" :key="item.id || index">
                <div class="file__info" :title="item.name">
                    <div class="file__type" :class="`file__type--${item.type}`"></div>
                    <div class="file__name">{{ item.name }}</div>
                </div>

                <div class="file__selector" v-if="documentTypes.length">
                    <VSelect class="file__select select select--default" :options="documentTypes"
                        :reduce="(type) => type.id" :getOptionLabel="(type) => type.name || type.code || 'Type'"
                        :modelValue="item.documentTypeId ?? defaultTypeId" :clearable="false" :appendToBody="true"
                        :calculatePosition="withPopper"
                        @update:modelValue="updateDocumentType(item.id || index, $event)" :placeholder="'Type'" />
                </div>

                <div v-if="deletable" class="file__action">
                    <button type="button" class="file__delete" title="Remove file"
                        :aria-label="`Remove ${item.name}`" @click="deleteFile(index)"></button>
                </div>
            </div>
        </div>

        <div class="drop__title">
            {{ $t(`dropFile.${"title"}`) }}
        </div>

        <div class="drop__actions" v-if="!disabled">
            <button type="button" class="drop__button button button--default button--shadow" @click="openFileDialog()">
                {{ $t(`dropFile.${"button"}`) }}
                <input type="file" class="drop__input" ref="inputFile" multiple @change="selectFile($event)" />
            </button>

            <button v-if="showSaveButton && attachments.length" type="button"
                class="drop__button drop__button--save button button--default button--shadow" @click="saveFiles()">
                Save
            </button>
        </div>

        <div v-else class="disabled-el">
            <button type="button" disabled class="drop__button button button--default button--shadow">
                {{ $t(`dropFile.${"button"}`) }}
                <input type="file" class="drop__input" ref="inputFile" />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { getPolicyDocumentTypes, normalizePolicyAttachment } from '@/helpers/policyFileAttachment';
import withPopper from '@/helpers/withPopper';

defineOptions({ name: 'PolicyDropFile' });

const props = defineProps({
    deletable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    modelValue: { type: Array, default: () => [] },
    documentTypes: { type: Array, default: () => [] },
    showSaveButton: { type: Boolean, default: true },
    showGlobalDropZone: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'input', 'save']);

const inputFile = ref(null);
const isDragOver = ref(false);
const isFileDraggedGlobally = ref(false);
const attachments = ref([]);
const dragCounter = ref(0); // Track nested drag events

const documentTypes = computed(() => {
    const items = Array.isArray(props.documentTypes) ? props.documentTypes : [];
    return getPolicyDocumentTypes(items);
});

const defaultTypeId = computed(() => {
    return documentTypes.value[0]?.id ?? null;
});

const emitFiles = (shouldEmit = false) => {
    if (!shouldEmit) return;
    emit('input', attachments.value);
    emit('update:modelValue', attachments.value);
};

// Check if dataTransfer contains files
const isFileDragEvent = (event) => {
    const dt = event.dataTransfer;
    if (!dt) return false;

    // Check for files in types array
    if (dt.types && Array.from(dt.types).includes('Files')) {
        return true;
    }

    // Fallback: check items property
    if (dt.items) {
        return Array.from(dt.items).some(item => item.kind === 'file');
    }

    return false;
};

const ensureItemType = (item, targetTypeId = null) => {
    const fallbackType = documentTypes.value[0] || null;
    const typeId = targetTypeId ?? item.documentTypeId ?? fallbackType?.id ?? null;

    return {
        ...item,
        documentTypeId: typeId,
        documentType: documentTypes.value.find((type) => type.id === typeId) || null,
    };
};

const addAttachments = (files) => {
    const normalizedFiles = Array.from(files || [])
        .filter((file) => file?.name)
        .map((file) => ensureItemType(normalizePolicyAttachment(file, documentTypes.value)));

    if (!normalizedFiles.length) return;

    attachments.value = [...attachments.value, ...normalizedFiles];
    emit('update:modelValue', attachments.value);
};

const updateDocumentType = (itemId, typeId) => {
    attachments.value = attachments.value.map((item) => {
        if ((item.id || item.name) === itemId || item.id === itemId) {
            const nextType = documentTypes.value.find((type) => type.id === typeId) || null;
            return { ...item, documentTypeId: typeId, documentType: nextType };
        }

        return item;
    });
    emit('update:modelValue', attachments.value);
    emitFiles(true);
};

const deleteFile = (index) => {
    attachments.value.splice(index, 1);
    emit('update:modelValue', attachments.value);
};

const saveFiles = () => {
    if (!attachments.value.length) return;
    emit('save', [...attachments.value]);
};

const openFileDialog = () => {
    if (inputFile.value) {
        inputFile.value.click();
    }
};

const selectFile = (event) => {
    addAttachments(event.target.files);
    // Reset input value to allow reselection and close file picker
    if (inputFile.value) {
        inputFile.value.value = '';
    }
};

const dropFile = (event) => {
    isDragOver.value = false;
    isFileDraggedGlobally.value = false;
    dragCounter.value = 0;

    event.preventDefault();

    if (!isFileDragEvent(event)) return;

    addAttachments(event.dataTransfer?.files);
    
    // Reset input value to allow reselection and close file picker if it was open
    if (inputFile.value) {
        inputFile.value.value = '';
    }
    isDragOver.value = false;
};

// Global drag event handlers
const handleGlobalDragEnter = (event) => {
    event.preventDefault();

    if (!props.showGlobalDropZone || !isFileDragEvent(event)) return;

    dragCounter.value++;
    isFileDraggedGlobally.value = true;
};

const handleGlobalDragOver = (event) => {
    event.preventDefault();

    if (!props.showGlobalDropZone || !isFileDragEvent(event)) return;

    // Keep overlay visible during drag over
    isFileDraggedGlobally.value = true;
    event.dataTransfer.dropEffect = 'copy';
};

const handleGlobalDragLeave = (event) => {
    event.preventDefault();

    dragCounter.value--;

    // Only hide when all nested drag events have left
    if (dragCounter.value === 0) {
        isFileDraggedGlobally.value = false;
    }
};

const handleGlobalDrop = (event) => {
    event.preventDefault();
    dragCounter.value = 0;
    isFileDraggedGlobally.value = false;
};

watch(() => props.modelValue, (newValue) => {
    if (!Array.isArray(newValue)) {
        attachments.value = [];
        return;
    }

    attachments.value = newValue.map((item) => ensureItemType(item, item.documentTypeId ?? item.document_type_id ?? null));
}, { immediate: true });

onMounted(() => {
    // Add global drag listeners
    if (props.showGlobalDropZone) {
        window.addEventListener('dragenter', handleGlobalDragEnter);
        window.addEventListener('dragover', handleGlobalDragOver);
        window.addEventListener('dragleave', handleGlobalDragLeave);
        window.addEventListener('drop', handleGlobalDrop);
    }
});

onUnmounted(() => {
    // Remove global drag listeners
    if (props.showGlobalDropZone) {
        window.removeEventListener('dragenter', handleGlobalDragEnter);
        window.removeEventListener('dragover', handleGlobalDragOver);
        window.removeEventListener('dragleave', handleGlobalDragLeave);
        window.removeEventListener('drop', handleGlobalDrop);
    }
});
</script>

<style lang="scss" scoped>
.drop-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.2s ease-in-out;

    &__content {
        background: var(--bg-1);
        border: 3px dashed var(--primary-color, #22db8e);
        border-radius: var(--radius-2, 12px);
        padding: 60px 40px;
        text-align: center;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
        pointer-events: none;
        min-width: 300px;
    }

    &__icon {
        width: 80px;
        height: 80px;
        margin: 0 auto 20px;
        background: url("../assets/icons/default/Bold 2px-file_add.svg") no-repeat center;
        background-size: contain;
        opacity: 0.8;
    }

    &__text {
        font-weight: 600;
        color: var(--text-2);
        margin: 0;
        font-size: var(--medium);
        font-family: var(--muli-text-semi-bold);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.drop {
    --upload-border-color: var(--border-10);
    --upload-header-bg: var(--bg-3);

    background: var(--bg-3);
    border-radius: var(--radius-1);
    // min-width: 228px;
    // max-width: 228px;
    // min-height: 220px;
    // max-height: 220px;
    width: 100%;
    max-width: 100%;
    min-height: auto;
    position: relative;
    padding: 15px 8px;
    box-sizing: border-box;
    overflow: auto;

    &--over {
        background: var(--bg-1);
        box-shadow: 0px 7px 20px var(--shadow-3);

        * {
            pointer-events: none;
        }
    }

    &__icon {
        width: 76px;
        height: 76px;
        position: relative;
        margin: 0 auto;
        margin-bottom: 30px;
        pointer-events: none;

        &::after,
        &::before {
            position: absolute;
            content: "";
            display: block;
            height: 100%;
            width: 100%;
        }

        &::after {
            background: url("../assets/icons/default/Bold 2px-arrow_up_down.svg") no-repeat center;
        }

        &::before {
            background: url("../assets/icons/default/Bold 2px-file_add.svg") no-repeat center;
        }
    }

    .drop__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        margin-top: 12px;
    }

    .files {
        overflow: auto;
        width: 100%;
        max-width: 100%;
        background: var(--bg-13);
        border-radius: var(--radius-1);
        border: 1px solid var(--upload-border-color);
        box-sizing: border-box;

        &__header,
        .file {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
        }

        &--deletable {
            .files__header,
            .file {
                grid-template-columns: minmax(0, 1fr) 72px;
            }
        }

        &--has-types {
            .files__header,
            .file {
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            }
        }

        &--has-types.files--deletable {
            .files__header,
            .file {
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 72px;
            }
        }

        &__header {
            background: var(--upload-header-bg);
            border-bottom: 1px solid var(--upload-border-color);
        }

        &__heading {
            min-width: 0;
            padding: 9px 12px;
            color: var(--text-2);
            font-family: var(--muli-text-semi-bold);
            font-size: var(--x-smaller);

            &+& {
                border-left: 1px solid var(--upload-border-color);
            }

            &--action {
                padding-right: 6px;
                padding-left: 6px;
                text-align: center;
            }
        }
    }

    .file {
        min-height: 54px;
        background: var(--bg-1);
        border-bottom: 1px solid var(--upload-border-color);

        &:last-child {
            border-bottom: 0;
        }

        &__info {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
            padding: 10px 12px;
        }

        &__name {
            min-width: 0;
            color: var(--text-2);
            font-size: var(--x-smaller);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &__selector {
            display: flex;
            align-items: center;
            min-width: 0;
            padding: 8px 12px;
            border-left: 1px solid var(--upload-border-color);
        }

        &__select {
            width: 100%;
            min-height: 30px;
            border-radius: 6px;
            border: 1px solid var(--upload-border-color);
            background: var(--bg-1);
            color: var(--text-2);
            padding: 0 8px;
            position: relative;
            z-index: 20;
        }

        &__action {
            display: flex;
            align-items: center;
            justify-content: center;
            border-left: 1px solid var(--upload-border-color);
            background: var(--upload-header-bg);
        }

        :deep(.vs__dropdown-menu) {
            z-index: 9999 !important;
            position: absolute !important;
            overflow: visible !important;
            max-width: min(360px, calc(100vw - 32px));
        }

        :deep(.vs__dropdown-option) {
            position: relative;
            z-index: 1;
        }

        &__delete {
            width: 32px;
            height: 32px;
            padding: 0;
            border: 1px solid transparent;
            border-radius: var(--radius-1);
            cursor: pointer;
            background: url("../assets/icons/default/x_delete_icon.svg") no-repeat center;
            background-size: 18px 18px;
            flex-shrink: 0;
            transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

            &:hover,
            &:focus-visible {
                background-color: var(--bg-1);
                border-color: var(--border-2);
                box-shadow: 0 2px 6px var(--shadow-6);
                outline: none;
            }
        }
    }

    &__title {
        color: var(--text-2);
        font-family: var(--muli-text-semi-bold);
        text-align: center;
        margin: 10px 0;
        font-size: var(--x-smaller);
    }

    &__button {
        height: 30px;
        width: 100px;
        font-family: var(--muli-text-regular);
        margin: auto;
        font-size: var(--x-smaller);
    }

    .drop__input {
        display: none;
    }

    .file__select {
        height: 25px;
        font-size: 12px;
    }
}

[data-theme="dark"] .drop {
    --upload-border-color: var(--border-14);
    --upload-header-bg: var(--bg-7);
}

@media (max-width: 640px) {
    .drop {
        padding: 12px 6px;

        .files {
            &__header {
                display: none;
            }

            .file,
            &--deletable .file,
            &--has-types .file,
            &--has-types.files--deletable .file {
                grid-template-columns: minmax(0, 1fr) 56px;
            }

            &:not(.files--deletable) .file {
                grid-template-columns: minmax(0, 1fr);
            }
        }

        .file {
            &__info {
                grid-column: 1;
                grid-row: 1;
                padding: 10px;
            }

            &__selector {
                grid-column: 1;
                grid-row: 2;
                display: grid;
                grid-template-columns: minmax(64px, auto) minmax(0, 1fr);
                gap: 8px;
                padding: 8px 10px 10px;
                border-top: 1px solid var(--upload-border-color);
                border-left: 0;

                &::before {
                    content: "File type";
                    align-self: center;
                    color: var(--text-2);
                    font-family: var(--muli-text-semi-bold);
                    font-size: var(--x-smaller);
                }
            }

            &__action {
                grid-column: 2;
                grid-row: 1;
            }

        }

        .files--has-types .file__action {
            grid-row: 1 / span 2;
        }

        .drop__actions {
            flex-wrap: wrap;
        }
    }

    .drop-overlay__content {
        width: calc(100vw - 32px);
        min-width: 0;
        padding: 40px 20px;
    }
}

@media (max-width: 380px) {
    .drop {
        .files {
            .file,
            &--deletable .file,
            &--has-types .file,
            &--has-types.files--deletable .file {
                grid-template-columns: minmax(0, 1fr) 48px;
            }
        }

        .file__selector {
            grid-template-columns: 1fr;
        }

        .drop__actions {
            align-items: stretch;

            .drop__button {
                width: 100%;
            }
        }
    }
}
</style>
